import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import I18n from 'I18n';
import { OLAF, SLINKY, CALYPSO_LIGHT, BATTLESHIP } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import HR from 'UIComponents/elements/HR';
import { getFilteredCharmap, getFrequentlyUsed, setFrequentlyUsed } from '../../utils/charmap';
import { memoize } from '../../utils/functions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `RichTextLib.charmap.${key}`;
const CharmapScrollContainer = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__CharmapScrollContainer",
  componentId: "sc-1ggmdk6-0"
})(["height:200px !important;overflow-y:scroll !important;position:relative !important;line-height:initial;margin-top:6px;margin-bottom:10px;margin-left:20px;padding-right:15px;user-select:none;"]);
const CategoryLabel = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__CategoryLabel",
  componentId: "sc-1ggmdk6-1"
})(["background-color:", " !important;color:", " !important;font-size:12px !important;line-height:12px;padding:4px 0 10px !important;position:sticky !important;top:0 !important;z-index:2;"], OLAF, SLINKY);
const CharacterItem = styled.span.withConfig({
  displayName: "CharmapInsertPopoverBody__CharacterItem",
  componentId: "sc-1ggmdk6-2"
})(["cursor:pointer !important;overflow:hidden;width:36px;height:36px;border-radius:18px;font-size:20px !important;font-weight:400;line-height:36px !important;text-align:center;display:inline-block;z-index:1;transition:background-color 0.2s;&:hover{background-color:", ";}"], CALYPSO_LIGHT);
const SelectedCharacterContainer = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__SelectedCharacterContainer",
  componentId: "sc-1ggmdk6-3"
})(["width:100%;height:24px;padding-left:15px;display:flex;"]);
const SelectedCharacter = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__SelectedCharacter",
  componentId: "sc-1ggmdk6-4"
})(["font-size:18px;font-weight:600;text-align:center;width:24px;overflow:visible;"]);
const SelectedCharacterName = styled(UITruncateString).withConfig({
  displayName: "CharmapInsertPopoverBody__SelectedCharacterName",
  componentId: "sc-1ggmdk6-5"
})(["font-size:12px;margin-left:8px;margin-right:12px;"]);
const HorizontalLine = styled(HR).withConfig({
  displayName: "CharmapInsertPopoverBody__HorizontalLine",
  componentId: "sc-1ggmdk6-6"
})(["background-color:", ";"], BATTLESHIP);
const memoizedGetFilteredCharmap = memoize(getFilteredCharmap);
const renderCategorizedCharmap = (charmap = {}, handleMouseOver, handleInsert) => Object.entries(charmap).map(([category, entries]) => {
  const Header = /*#__PURE__*/_jsx(CategoryLabel, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey(`categories.${category}`)
    })
  });
  let Items;
  if (!entries.length) {
    Items = [/*#__PURE__*/_jsx("div", {
      children: `${I18n.text(i18nKey('noResultsFound'))} ☹`
    }, "empty-results")];
  } else {
    Items = entries.map(({
      charCode,
      label
    } = {}) => charCode && /*#__PURE__*/_jsx(CharacterItem, {
      onMouseOver: () => handleMouseOver(charCode, label),
      onClick: () => {
        handleInsert(charCode);
      },
      children: String.fromCharCode(charCode)
    }, `char-code-${charCode}`));
  }
  const key = `charmap-category-${category}`;
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": key,
    children: [Header, Items]
  }, key);
});
export default function CharmapInsertPopoverBody(props) {
  const {
    onInsert,
    storeHandlers: {
      init,
      getter,
      setter
    } = {}
  } = props;
  const [selectedChar, setSelectedChar] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [frequentlyUsedCharmap, setFrequentlyUsedCharmap] = useState({});
  const searchInputRef = useRef(null);
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      return;
    };
  }, []);

  // Initialize the charmap store on mount
  useEffect(() => {
    if (init) {
      init(() => {
        if (isMounted.current) {
          setFrequentlyUsedCharmap(getFrequentlyUsed(getter));
        }
      });
    }
    // Focus on search input
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [init, getter]);
  const handleSearchChange = ({
    target: {
      value
    }
  }) => {
    setSearchTerm(value);
    setSelectedChar({});
  };
  const handleMouseOver = (charCode, name) => {
    setSelectedChar({
      char: String.fromCharCode(charCode),
      name
    });
  };
  const handleInsert = useCallback(charCode => {
    onInsert(charCode);
    setFrequentlyUsed(setter, charCode);
    setFrequentlyUsedCharmap(getFrequentlyUsed(getter));
    // Refocus on the search input
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [onInsert, searchInputRef, setter, getter]);
  const preventDefaultClick = evt => {
    // Don't allow the UIDropdown to default close on click
    if (evt.nativeEvent && evt.nativeEvent.stopImmediatePropagation) {
      evt.nativeEvent.stopImmediatePropagation();
    }
  };
  const FrequentlyUsedGrid = useMemo(() => {
    return !searchTerm && renderCategorizedCharmap(frequentlyUsedCharmap, handleMouseOver, handleInsert);
  }, [searchTerm, frequentlyUsedCharmap, handleInsert]);

  // Only regenerate charmap grid when the searchTerm has changed
  const CharmapGrid = useMemo(() => {
    const charmap = memoizedGetFilteredCharmap(searchTerm);
    return renderCategorizedCharmap(charmap, handleMouseOver, handleInsert);
  }, [searchTerm, handleInsert]);
  return /*#__PURE__*/_jsxs("div", {
    className: "small-popover-body",
    onClick: preventDefaultClick,
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": "search",
      className: "p-all-5",
      children: /*#__PURE__*/_jsx(UISearchInput, {
        "data-test-id": "charmap-search-input",
        placeholder: I18n.text(i18nKey('searchPlaceholder')),
        icon: false,
        clearable: false,
        onChange: handleSearchChange,
        inputRef: searchInputRef
      })
    }), /*#__PURE__*/_jsx(HorizontalLine, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(UIScrollContainer, {
      "data-test-id": "charmap-scroll-container",
      scrollDirection: "vertical",
      maxHeight: 200,
      size: 0,
      ScrollContainer: CharmapScrollContainer,
      children: [FrequentlyUsedGrid, CharmapGrid]
    }), /*#__PURE__*/_jsx(HorizontalLine, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(SelectedCharacterContainer, {
      className: "m-y-2",
      children: [/*#__PURE__*/_jsx(SelectedCharacter, {
        children: selectedChar.char || ''
      }), /*#__PURE__*/_jsx(SelectedCharacterName, {
        children: selectedChar.name || ''
      })]
    })]
  });
}
CharmapInsertPopoverBody.propTypes = {
  onInsert: PropTypes.func.isRequired,
  storeHandlers: PropTypes.shape({
    init: PropTypes.func.isRequired,
    getter: PropTypes.func.isRequired,
    setter: PropTypes.func.isRequired
  }).isRequired
};